// @givmo/employer-module — sign-up CHOOSER screen (/sign-up). Ports `sign_up_main_screen.dart`.
//
// "Create your admin account" + "Register with your work email" (→ /sign-up/details) / "Register with
// Google" / "Register with Microsoft" + the Terms/Privacy acceptance warning. Provider SIGN-UP
// expects a NEW user: on success @givmo/auth returns the credential and we send the user to the
// details screen to finish company registration (the `unregistered` landing); an EXISTING account
// surfaces "User is already registered. Please sign in." (the package signs out + throws it).
//
// Rendered by both hosts (EP-MNT8): their auth, addresses, look and frame come from `useSignUpHost()`.

import { useCallback, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Button, ErrorMessage, ProviderButton, testIdProp } from '@givmo/design-system';
import { needsBackendOtpVerification, type AdminProviderId } from '@givmo/auth';
import { SignUpHeading, useSignUpHost } from './sign-up-host';
import { TermsPolicyWarning } from './terms-policy-warning';
import { useAuthError } from './use-auth-error';

export function SignUpMainScreen() {
  const { auth, paths, look, Shell } = useSignUpHost();
  const { signUpWithProvider } = auth;
  const navigate = useNavigate();
  // EP-SUITE2-FIX6: the provider sign-UP signs its account in (and, for an existing one, out again)
  // before it can fail, so its error must outlive the identity boundary's remount — the door's key.
  const { error, clearError, surface } = useAuthError('sign-up');
  const [pendingProvider, setPendingProvider] = useState<AdminProviderId | null>(null);

  const onProvider = useCallback(
    async (provider: AdminProviderId) => {
      clearError();
      setPendingProvider(provider);
      try {
        const credential = await signUpWithProvider(provider);
        // New provider user authenticated. A microsoft.com email arrives UNVERIFIED (GCIP does not
        // treat Microsoft as attesting), and company onboarding needs a trustworthy address (the
        // EP-SUITE2 endpoints refuse unverified callers) — so those users verify by OTP FIRST and
        // the OTP screen re-resolves onward to the details screen. Google arrives verified and goes
        // straight to details. The EXPLICIT `provider` flag makes each destination take the
        // provider-registration path.
        if (needsBackendOtpVerification(credential.user)) {
          await navigate({ to: paths.signUpOtp, search: { provider: true } });
        } else {
          await navigate({ to: paths.signUpDetails, search: { provider: true } });
        }
      } catch (thrown) {
        surface(thrown);
      } finally {
        setPendingProvider(null);
      }
    },
    [signUpWithProvider, navigate, paths, clearError, surface],
  );

  return (
    <Shell testId="page-sign-up">
      <button
        type="button"
        onClick={() => void navigate({ to: paths.loginHome })}
        className={look.textButton}
        {...testIdProp('auth-signup-chooser-back')}
      >
        Back
      </button>

      <SignUpHeading heading={<h1 className={look.heading}>Create your admin account</h1>} />

      {/* The three ways to register are one set, stacked as the host's sign-in door stacks its ways
          in (`look.choiceGroup`); where that door's chooser splits its email way from the providers,
          this one does too (`look.choiceDivider`: the portal's). */}
      <div className={look.choiceGroup}>
        <Button
          {...look.primary}
          onClick={() => void navigate({ to: paths.signUpDetails })}
          testId="auth-signup-email"
        >
          Register with your work email
        </Button>

        {look.choiceDivider && (
          <div className="flex items-center gap-12 py-4" aria-hidden>
            <span className="h-px flex-1 bg-border-subtle" />
            <span className="font-sans text-s12 font-w500 tracking-normal text-text-tertiary">
              or continue with
            </span>
            <span className="h-px flex-1 bg-border-subtle" />
          </div>
        )}

        <ProviderButton
          provider="google"
          label="Register with Google"
          loading={pendingProvider === 'google'}
          disabled={pendingProvider !== null}
          onClick={() => void onProvider('google')}
          testId="login-google-button"
        />
        <ProviderButton
          provider="microsoft"
          label="Register with Microsoft"
          loading={pendingProvider === 'microsoft'}
          disabled={pendingProvider !== null}
          onClick={() => void onProvider('microsoft')}
          testId="login-microsoft-button"
        />
      </div>

      {error !== null && <ErrorMessage testId="auth-error">{error}</ErrorMessage>}

      <TermsPolicyWarning action="registering" />
    </Shell>
  );
}
